import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, map, shareReplay, skip } from 'rxjs';
import { LocaleApiService } from './locale-api.service';
import { ProtectedTranslateService } from './protected-translate.service';

export const DEFAULT_LOCALE = 'de-DE';
const APP_LOCALE_LS_KEY = 'appLocale';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private api = inject(LocaleApiService);
  private translate = inject(ProtectedTranslateService);

  constructor(private destroyRef: DestroyRef) {
    this.listenToLanguageSwitch();
  }

  /** We determine the locale during app initialization and assign it to the TranslateModule,
   * which then serves as the single source of truth for locale across the app. */
  async localeInitializer(): Promise<void> {
    const fromLS = localStorage.getItem(APP_LOCALE_LS_KEY);
    const locale = fromLS ? fromLS : await firstValueFrom(this.defaultAppLocale$);
    this.translate.forceUse(locale);
  }

  /** All possible variants of full locales which we retrieve from Phrase, as examples */
  appLocales$ = this.api.getAppLocales().pipe(
    map((locales) =>
      locales
        .filter((locale) => !!locale.split('-')[1])
        .sort((prev, curr) => this.getCountryCode(prev).localeCompare(this.getCountryCode(curr))),
    ),
    shareReplay(1),
  );

  private listenToLanguageSwitch() {
    // We are skipping language changes during the initialization phase.
    this.translate.onLangChange.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      localStorage.setItem(APP_LOCALE_LS_KEY, event.lang);
      location.reload();
    });
  }

  private isShortLocale(locale: string): boolean {
    return locale.split('-')[1] === undefined;
  }

  private getCountryCode(locale: string): string {
    return locale.split('-')[1]?.toLowerCase() ?? '';
  }

  /**
   *  Always returns the full locale in the format 'xx-XX'.
   *  Even if the client uses a short locale, we resolve it to the full one
   *  to stay consistent with the language switcher, which only displays languages with country codes
   */
  private defaultAppLocale$ = this.appLocales$.pipe(
    map((locales) => {
      const appLocalesSet = new Set(locales);

      for (const userLocale of navigator.languages) {
        if (appLocalesSet.has(userLocale)) return userLocale;

        if (this.isShortLocale(userLocale)) {
          const fallbackMap = this.buildShortLocaleFallbackMap(locales);
          return fallbackMap[userLocale] ?? DEFAULT_LOCALE;
        }
      }

      return DEFAULT_LOCALE;
    }),
  );

  private buildShortLocaleFallbackMap(appLocales: string[]): Record<string, string | undefined> {
    const langToLocaleMap: Record<string, string | undefined> = {};

    for (const appLocale of appLocales) {
      const [lang, region] = appLocale.split('-');
      const current = langToLocaleMap[lang];
      const isDEFallback = current && !this.isLocaleCanonical(current) && region === 'DE';

      if (this.isLocaleCanonical(appLocale) || isDEFallback || !current) {
        langToLocaleMap[lang] = appLocale;
      }
    }

    return langToLocaleMap;
  }

  private isLocaleCanonical(fullLocale: string): boolean {
    const [lang, region] = fullLocale.split('-');
    return lang.toUpperCase() === region;
  }
}
